import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, ButtonBase } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { FONT_SIZE, ICON_SIZE, NEUTRAL } from "../../../../theme/tokens";
import type { OpenShiftRequest, ReplaceShiftRequest } from "../../../../utils/models";
import { TOUCH_TARGET_HEIGHT_SX } from "../../../UIToolkit/touchTarget";
import { GAPS_SHORTLIST_SIZE } from "../gapsRowLayout";
import { GapsApplicantLine } from "./GapsApplicantLine";
import type { GapsApplicant } from "./useGapsApplicants";

type Props = {
	request: OpenShiftRequest | ReplaceShiftRequest;
	responseCount: number;
	/** The answers show; folded, the card offers one "see who" line instead. */
	isExpanded: boolean;
	applicants: GapsApplicant[];
	/** The user asked to see who answered. */
	onReview: () => void;
	formatHours: (start: DateTime, end: DateTime) => string;
	onApprove: (request: OpenShiftRequest | ReplaceShiftRequest, applicant: GapsApplicant) => void;
	testId: string;
};

/** The quiet full-width rows under a card's answers — "3 people asked · see who", "Show 2 more". */
const footerRowSx = {
	width: "100%",
	display: "flex",
	alignItems: "center",
	px: 1.5,
	py: 1,
	borderTop: `1px solid ${NEUTRAL[150]}`,
	bgcolor: NEUTRAL[50],
	fontSize: FONT_SIZE.CAPTION,
	fontWeight: 600,
	color: "primary.dark",
} as const;

/**
 * A request card's answers: folded, one "N people asked · see who" line; open, one line per response with a labelled
 * Approve — three at first and the rest behind "Show N more". Nothing for a request nobody answered.
 */
export const GapsRequestAnswers: FC<Props> = ({ request, responseCount, isExpanded, applicants, onReview, formatHours, onApprove, testId }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const [isShowingAll, setIsShowingAll] = useState<boolean>(false);
	const visibleApplicants = isShowingAll ? applicants : applicants.slice(0, GAPS_SHORTLIST_SIZE);
	const hiddenCount = applicants.length - visibleApplicants.length;

	if (responseCount === 0) return null;
	if (!isExpanded) {
		return (
			<ButtonBase
				data-testid={`${testId}-review`}
				onClick={() => {
					track("gaps_requests_review_opened", { department_id: chosenDepartment.department_id, request_id: request.id, responses: responseCount });
					onReview();
				}}
				aria-expanded={false}
				sx={[footerRowSx, { justifyContent: "space-between" }, TOUCH_TARGET_HEIGHT_SX]}
			>
				{t("gaps.requests.review", { count: responseCount })}
				<ExpandMoreIcon aria-hidden sx={{ fontSize: ICON_SIZE.MENU_ROW }} />
			</ButtonBase>
		);
	}
	return (
		<Box data-testid={`${testId}-applicants`} sx={{ borderTop: `1px solid ${NEUTRAL[150]}` }}>
			{visibleApplicants.map((applicant) => (
				<GapsApplicantLine
					key={applicant.rowKey}
					applicant={applicant}
					formatHours={formatHours}
					onApprove={(picked) => onApprove(request, picked)}
					dataTestId={`${testId}-applicant-${applicant.nurseId}`}
				/>
			))}
			{hiddenCount > 0 && (
				<ButtonBase
					data-testid={`${testId}-show-more`}
					onClick={() => {
						track("gaps_requests_show_more", { department_id: chosenDepartment.department_id, request_id: request.id, hidden: hiddenCount });
						setIsShowingAll(true);
					}}
					sx={[footerRowSx, { justifyContent: "flex-start" }, TOUCH_TARGET_HEIGHT_SX]}
				>
					{t("gaps.requests.showMore", { count: hiddenCount })}
				</ButtonBase>
			)}
		</Box>
	);
};
